import { Table, Tag, Tooltip, Typography, type TableColumnsType } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import type { RecentError } from '@/types/stats';

const COORD_PRECISION = 5;
const HTTP_SERVER_ERROR = 500;
const HTTP_CLIENT_ERROR = 400;

function formatCoord(lat: number | null, lon: number | null): string | null {
  if (lat === null || lon === null) {
    return null;
  }
  return `${lat.toFixed(COORD_PRECISION)}, ${lon.toFixed(COORD_PRECISION)}`;
}

interface RecentErrorsTableProps {
  readonly items: readonly RecentError[];
  readonly loading: boolean;
}

/**
 * Shared recent-errors table for the Stats page and the Dashboard's
 * expandable error panel. Surfaces the diagnostic fields of
 * `api_request_log`: when, which endpoint, HTTP status, machine error
 * code, the origin/destination coordinates of the failing request (so an
 * operator can reproduce it) and how long it took.
 */
export default function RecentErrorsTable({ items, loading }: RecentErrorsTableProps) {
  const { t } = useTranslation();
  const dash = <Typography.Text type="secondary">—</Typography.Text>;

  const columns: TableColumnsType<RecentError> = [
    {
      title: t('stats.recentErrors.columns.time'),
      dataIndex: 'requested_at',
      width: 160,
      render: (value: string) => dayjs(value).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: t('stats.recentErrors.columns.endpoint'),
      dataIndex: 'endpoint',
      width: 150,
      render: (value: string) => <Tag>{value}</Tag>,
    },
    {
      title: t('stats.recentErrors.columns.status'),
      dataIndex: 'status_code',
      width: 70,
      align: 'right',
      render: (value: number) => (
        <Tag
          color={
            value >= HTTP_SERVER_ERROR
              ? 'red'
              : value >= HTTP_CLIENT_ERROR
                ? 'orange'
                : 'gold'
          }
        >
          {value}
        </Tag>
      ),
    },
    {
      title: t('stats.recentErrors.columns.code'),
      dataIndex: 'error_code',
      render: (value: string | null) =>
        value ? (
          <Typography.Text code style={{ fontSize: 12 }}>
            {value}
          </Typography.Text>
        ) : (
          dash
        ),
    },
    {
      title: t('stats.recentErrors.columns.from'),
      key: 'origin',
      width: 175,
      render: (_, row) => {
        const coord = formatCoord(row.origin_lat, row.origin_lon);
        return coord ? (
          <Typography.Text copyable style={{ fontSize: 12 }}>
            {coord}
          </Typography.Text>
        ) : (
          dash
        );
      },
    },
    {
      title: t('stats.recentErrors.columns.to'),
      key: 'destination',
      width: 175,
      render: (_, row) => {
        const coord = formatCoord(row.destination_lat, row.destination_lon);
        return coord ? (
          <Typography.Text copyable style={{ fontSize: 12 }}>
            {coord}
          </Typography.Text>
        ) : (
          dash
        );
      },
    },
    {
      title: t('stats.recentErrors.columns.freshness'),
      dataIndex: 'freshness',
      width: 90,
      render: (value: string | null) => value ?? dash,
    },
    {
      title: t('stats.recentErrors.columns.cacheHit'),
      dataIndex: 'cache_hit',
      width: 80,
      align: 'center',
      render: (value: boolean | null) =>
        value === null ? (
          dash
        ) : value ? (
          <Tag color="green">{t('common.yes')}</Tag>
        ) : (
          <Tag>{t('common.no')}</Tag>
        ),
    },
    {
      title: t('stats.recentErrors.columns.client'),
      key: 'client',
      width: 150,
      render: (_, row) => {
        const info = row.client_info;
        if (!info || (!info.api_key_hash && !info.user_agent)) {
          return dash;
        }
        const primary = info.api_key_hash ?? info.user_agent ?? '';
        return (
          <Tooltip title={info.user_agent ?? info.api_key_hash}>
            <Typography.Text code style={{ fontSize: 11 }} ellipsis>
              {primary}
            </Typography.Text>
          </Tooltip>
        );
      },
    },
    {
      title: t('stats.recentErrors.columns.durationMs'),
      dataIndex: 'duration_ms',
      width: 110,
      align: 'right',
      render: (value: number) => `${value} ms`,
    },
  ];

  return (
    <Table<RecentError>
      size="small"
      rowKey={(row) => `${row.requested_at}-${row.endpoint}`}
      columns={columns}
      dataSource={items as RecentError[]}
      loading={loading}
      pagination={false}
      scroll={{ x: 'max-content' }}
    />
  );
}
